/**
 * SunEditor mounts dialogs, submenus and controllers inside the editor's
 * relative wrapper. Parent modals/scroll containers with overflow:auto|hidden
 * still clip those layers even when EditorShell itself is overflow:visible.
 *
 * This helper portals those floating layers to a body-level .sun-editor host
 * (so upstream SunEditor CSS still matches) and repositions menus/controllers
 * with position:fixed using viewport coordinates.
 *
 * @param {object} core SunEditor core instance from getSunEditorInstance
 * @returns {() => void} cleanup
 */

const FLOAT_Z_INDEX = '11000'; // above flexibull Modal overlay (z-index: 1000)
const STYLE_ID = 'saf-suneditor-float-host-styles';

const ensureHostStyles = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    .saf-suneditor-float-host {
      font-family: Helvetica, Arial, sans-serif;
    }
    .saf-suneditor-float-host .se-list-layer,
    .saf-suneditor-float-host .se-controller,
    .saf-suneditor-float-host .se-dialog,
    .saf-suneditor-float-host .se-file-browser {
      pointer-events: auto;
    }
  `;
  document.head.appendChild(style);
};

export const escapeSunEditorClipping = (core) => {
  if (
    typeof document === 'undefined' ||
    !core ||
    !core.context ||
    !core.context.element
  ) {
    return () => {};
  }

  ensureHostStyles();

  const { util } = core;
  const relative = core.context.element.relative;
  const dialogArea =
    core.context.dialog && core.context.dialog.modalArea
      ? core.context.dialog.modalArea
      : null;

  const host = document.createElement('div');
  host.className = 'sun-editor saf-suneditor-float-host';
  host.setAttribute('data-saf-suneditor-float', 'true');
  Object.assign(host.style, {
    position: 'fixed',
    inset: '0',
    width: '100%',
    height: '100%',
    zIndex: FLOAT_Z_INDEX,
    pointerEvents: 'none',
    overflow: 'visible',
  });
  document.body.appendChild(host);

  const enablePointerEvents = (el) => {
    if (el) el.style.pointerEvents = 'auto';
  };

  if (dialogArea) {
    host.appendChild(dialogArea);
    enablePointerEvents(dialogArea);
  }

  // Keep popup dialogs viewport-fixed (default is already 'full', set explicitly).
  if (core.options) {
    core.options.popupDisplay = 'full';
  }

  const moveToHost = (el) => {
    if (el && el.parentNode !== host) {
      host.appendChild(el);
    }
    enablePointerEvents(el);
  };

  const originalSetMenuPosition = core._setMenuPosition;
  core._setMenuPosition = function patchedSetMenuPosition(element, menu) {
    moveToHost(menu);

    menu.style.position = 'fixed';
    menu.style.visibility = 'hidden';
    menu.style.display = 'block';
    menu.style.height = '';
    menu.style.overflowY = '';
    menu.style.zIndex = FLOAT_Z_INDEX;
    util.addClass(element, 'on');

    const btnRect = element.getBoundingClientRect();
    const menuW = menu.offsetWidth;
    const menuH = menu.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const margin = 8;

    let left = btnRect.left;
    if (left + menuW > vw - margin) {
      left = Math.max(margin, vw - menuW - margin);
    }
    if (left < margin) left = margin;

    let top = btnRect.bottom + 2;
    if (top + menuH > vh - margin) {
      const above = btnRect.top - menuH - 2;
      if (above >= margin) {
        top = above;
      } else {
        const spaceBelow = Math.max(0, vh - top - margin);
        const spaceAbove = Math.max(0, btnRect.top - margin);
        if (spaceBelow >= spaceAbove) {
          menu.style.height = `${Math.max(120, spaceBelow)}px`;
          menu.style.overflowY = 'auto';
        } else {
          top = margin;
          menu.style.height = `${Math.max(120, spaceAbove)}px`;
          menu.style.overflowY = 'auto';
        }
      }
    }

    menu.style.left = `${left}px`;
    menu.style.top = `${top}px`;
    menu.style.visibility = '';
  };

  const originalSetControllerPosition = core.setControllerPosition;
  core.setControllerPosition = function patchedSetControllerPosition(
    controller,
    referEl,
    position,
    addOffset
  ) {
    const offset = addOffset || { left: 0, top: 0 };
    moveToHost(controller);

    controller.style.position = 'fixed';
    controller.style.visibility = 'hidden';
    controller.style.display = 'block';
    controller.style.zIndex = FLOAT_Z_INDEX;

    const rect = referEl.getBoundingClientRect();
    const topMargin =
      position === 'top'
        ? -(controller.offsetHeight + 2)
        : referEl.offsetHeight + 12;

    let top = rect.top + topMargin + (offset.top || 0);
    let left = rect.left + (offset.left || 0);
    const controllerW = controller.offsetWidth;
    const margin = 8;

    if (left + controllerW > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - controllerW - margin);
    }
    if (left < margin) left = margin;
    if (top < margin) top = margin;

    controller.style.top = `${top}px`;
    controller.style.left = `${left}px`;

    const arrow = util.hasClass(controller.firstElementChild, 'se-arrow')
      ? controller.firstElementChild
      : null;
    if (arrow) arrow.style.left = '20px';

    controller.style.visibility = '';
  };

  // Controllers / file-browser nodes already appended under relative — lift any
  // that are currently present so the first open is not clipped.
  if (relative) {
    Array.from(relative.children).forEach((child) => {
      if (
        child.classList &&
        (child.classList.contains('se-controller') ||
          child.classList.contains('se-dialog') ||
          child.classList.contains('se-file-browser'))
      ) {
        moveToHost(child);
      }
    });
  }

  return () => {
    core._setMenuPosition = originalSetMenuPosition;
    core.setControllerPosition = originalSetControllerPosition;
    if (host.parentNode) {
      host.parentNode.removeChild(host);
    }
  };
};
